import { useMemo } from 'react'
import { useDynamicConfig } from '@statsig/react-bindings'
import { DynamicConfigName } from 'lib/statsig/feature_gates'
import { useAuthContext } from 'components/auth'

export const DEFAULT_SHIFT_LEAD_MIN_WORKERS = 20

/**
 * Returns the minimum number of workers required before shift leads can be
 * requested for the current company.
 *
 * Statsig dynamic config format:
 * {
 *   defaultMinWorkers: 20,
 *   companyOverrides: { "123": 5, "456": 10 },
 *   companyGroupOverrides: { "6": 10 }
 * }
 */
export function useShiftLeadMinWorkers() {
  const config = useDynamicConfig(DynamicConfigName.ShiftLeadMinWorkers)
  const { company } = useAuthContext()

  return useMemo(() => {
    if (!company) {
      return DEFAULT_SHIFT_LEAD_MIN_WORKERS
    }

    const { id, companyGroup } = company
    const companyGroupId = companyGroup?.id
    const companyOverrides = config.get('companyOverrides', {}) as Record<
      string,
      number
    >
    const companyGroupOverrides = config.get(
      'companyGroupOverrides',
      {}
    ) as Record<string, number>

    if (
      companyGroupId &&
      companyGroupOverrides[companyGroupId.toString()] !== undefined
    ) {
      return companyGroupOverrides[companyGroupId.toString()]
    }

    if (id && companyOverrides[id.toString()] !== undefined) {
      return companyOverrides[id.toString()]
    }

    return config.get('defaultMinWorkers', DEFAULT_SHIFT_LEAD_MIN_WORKERS)
  }, [company, config])
}
